import { TrafficNode } from './trafficNode';
import { Platform } from '../platform';
import { Location } from '../location';
import { TRAFFICNODE_TYPE, STOP_SEQUENCE, OCCUPANCY_TYPE } from '../../enums/model.enums';
import { HourlyWeather } from '../weather/hourlyWeather';
import { Line } from '../line';
/**
 * class {TimetableTrafficNode}
 * should be used to display trafficNode related information extended with {Platform} and arrival/departure date-time information
 * @extends {TrafficNode}
 */
export class TimetableTrafficNode extends TrafficNode {
    /**
     * constructor of class
     * please use factory instead
     * @see {this.factory)
     * @param id
     * @param name
     * @param city
     * @param type
     * @param location
     * @param weather
     * @param arrivalDateTime
     * @param departureDateTime
     * @param platform
     * @param occupancy
     */
    constructor(id, name, city, type, location, weather, excludeInTrip, arrivalDateTime, departureDateTime, actualArrivalDateTime, actualDepartureDateTime, platform, positionInSequence, tariffZone, servingLines, occupancy, metaData) {
        super(id, name, city, type, location, weather, servingLines, metaData);
        this._arrivalDateTime = arrivalDateTime;
        this._departureDateTime = departureDateTime;
        this._actualArrivalDateTime = actualArrivalDateTime;
        this._actualDepartureDateTime = actualDepartureDateTime;
        this._platform = platform;
        this._positionInSequence = positionInSequence;
        this._tariffZone = tariffZone;
        this._excludeInTrip = excludeInTrip;
        this._occupancy = occupancy;
    }
    /**
     * Factory method to create an instance of this class without
     * having to correctly fill the constructor.
     * !!! You should always use this instead of the constructor !!!
     * @param values
     * @returns {TimetableTrafficNode}
     */
    static factory(values) {
        const sanitizedValues = {};
        sanitizedValues.id = values.id || '';
        sanitizedValues.name = values.name || '';
        sanitizedValues.city = values.city || '';
        sanitizedValues.type = values.type !== undefined ? values.type : TRAFFICNODE_TYPE.ANY;
        sanitizedValues.location = values.location ? Location.factory(values.location) : Location.factory({});
        sanitizedValues.excludeInTrip = values.excludeInTrip !== undefined ? values.excludeInTrip : false;
        sanitizedValues.weather = [];
        if (values.weather) {
            for (const weather of values.weather) {
                sanitizedValues.weather.push(HourlyWeather.factory(weather));
            }
        }
        sanitizedValues.arrivalDateTime = values.arrivalDateTime !== undefined ? new Date(values.arrivalDateTime) : undefined;
        sanitizedValues.departureDateTime = values.departureDateTime !== undefined ? new Date(values.departureDateTime) : undefined;
        sanitizedValues.actualArrivalDateTime = values.actualArrivalDateTime !== undefined ? new Date(values.actualArrivalDateTime) : undefined;
        sanitizedValues.actualDepartureDateTime = values.actualDepartureDateTime !== undefined
            ? new Date(values.actualDepartureDateTime)
            : undefined;
        sanitizedValues.platform = values.platform ? Platform.factory(values.platform) : Platform.factory({});
        sanitizedValues.metaData = values.metaData !== undefined ? values.metaData : {};
        sanitizedValues.positionInSequence = values.positionInSequence !== undefined
            ? values.positionInSequence
            : STOP_SEQUENCE.ONWARD;
        sanitizedValues.tariffZone = values.tariffZone || [];
        sanitizedValues.servingLines = [];
        if (values.servingLines) {
            for (const servingLine of values.servingLines) {
                sanitizedValues.servingLines.push(Line.factory(servingLine));
            }
        }
        sanitizedValues.occupancy = values.occupancy !== undefined ? values.occupancy : OCCUPANCY_TYPE.UNKNOWN;
        return new TimetableTrafficNode(sanitizedValues.id, sanitizedValues.name, sanitizedValues.city, sanitizedValues.type, sanitizedValues.location, sanitizedValues.weather, sanitizedValues.excludeInTrip, sanitizedValues.arrivalDateTime, sanitizedValues.departureDateTime, sanitizedValues.actualArrivalDateTime, sanitizedValues.actualDepartureDateTime, sanitizedValues.platform, sanitizedValues.positionInSequence, sanitizedValues.tariffZone, sanitizedValues.servingLines, sanitizedValues.occupancy, sanitizedValues.metaData);
    }
    /**
     * gets the platform information for this trafficNode
     * @returns {Platform}
     */
    get platform() {
        return this._platform;
    }
    /**
     * sets the platform information for this trafficNode
     * @param value
     */
    set platform(value) {
        this._platform = value;
    }
    /**
     * gets the departure date-time for this trafficNode (use with connection or timetableEvent information)
     * @returns {Date}
     */
    get departureDateTime() {
        return this._departureDateTime;
    }
    /**
     * sets the departure date-time for this trafficNode (use with connection or timetableEvent information)
     * @param value
     */
    set departureDateTime(value) {
        this._departureDateTime = value;
    }
    /**
     * gets the arrival date-time for this trafficNode (use with connection or timetableEvent information)
     * @returns {Date}
     */
    get arrivalDateTime() {
        return this._arrivalDateTime;
    }
    /**
     * sets the arrival date-time for this trafficNode (use with connection or timetableEvent information)
     * @param value
     */
    set arrivalDateTime(value) {
        this._arrivalDateTime = value;
    }
    /**
     * gets the real arrival date-time for this trafficNode (use with connection or timetableEvent information)
     * @returns {Date}
     */
    get actualArrivalDateTime() {
        return this._actualArrivalDateTime;
    }
    /**
     * sets the real arrival date-time for this trafficNode (use with connection or timetableEvent information)
     * @param value
     */
    set actualArrivalDateTime(value) {
        this._actualArrivalDateTime = value;
    }
    /**
     * gets the real departure date-time for this trafficNode (use with connection or timetableEvent information)
     * @returns {Date}
     */
    get actualDepartureDateTime() {
        return this._actualDepartureDateTime;
    }
    /**
     * sets the real departure date-time for this trafficNode (use with connection or timetableEvent information)
     * @param value
     */
    set actualDepartureDateTime(value) {
        this._actualDepartureDateTime = value;
    }
    /**
     * gets the position in the sequence of stops in the line for this node
     * @returns {STOP_SEQUENCE}
     */
    get positionInSequence() {
        return this._positionInSequence;
    }
    /**
     * sets the position in the sequence of stops in the line for this node
     * @param value
     */
    set positionInSequence(value) {
        this._positionInSequence = value;
    }
    /**
     * gets the tarif zone of stops in the line for this node
     * @returns {string}
     */
    get tariffZone() {
        return this._tariffZone;
    }
    /**
     * sets the tarif zone of stops in the line for this node
     * @param value
     */
    set tariffZone(value) {
        this._tariffZone = value;
    }
    /**
     * gets the boolean of whether the node is excluded from the trip
     * @returns {boolean} true if the node is excluded
     */
    get excludeInTrip() {
        return this._excludeInTrip;
    }
    /**
     * sets the boolean of whether the node is excluded from the trip
     * @param value boolean
     */
    set excludeInTrip(value) {
        this._excludeInTrip = value;
    }
    /**
     * gets the occupancy type for this node
     * @returns {OCCUPANCY_TYPE}
     */
    get occupancy() {
        return this._occupancy;
    }
    /**
     * sets the occupancy type for this node
     * @param value
     */
    set occupancy(value) {
        this._occupancy = value;
    }
}
//# sourceMappingURL=timetableTrafficNode.js.map